import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Logo } from './ui/Logo'
import { useI18n } from '../i18n'
import { useWebsite } from '../hooks/use-website'
import { getPublicUrl } from '../helper/website-url'
import { ArrowLeft, Globe, Sparkles, Eye, BarChart3, ExternalLink, Clock, Copy } from 'lucide-react'

export default function MyWebsitePage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { website, loading, fetchWebsite, publishWebsite } = useWebsite()
  const [publishing, setPublishing] = useState(false)

  useEffect(() => {
    fetchWebsite()
  }, [fetchWebsite])

  const handlePublish = async () => {
    setPublishing(true)
    await publishWebsite()
    await fetchWebsite()
    setPublishing(false)
  }

  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
    DRAFT: { label: t('草稿', 'Draft'), color: 'text-gray-500', bg: 'bg-gray-100' },
    GENERATING: { label: t('生成中', 'Generating'), color: 'text-violet-500', bg: 'bg-violet-50' },
    PUBLISHED: { label: t('已发布', 'Live'), color: 'text-emerald-500', bg: 'bg-emerald-50' },
    UNPUBLISHED: { label: t('已下线', 'Offline'), color: 'text-amber-500', bg: 'bg-amber-50' },
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sticky top-0 z-30">
        <Link to="/workspace" className="flex items-center gap-2 text-lg font-semibold mr-6 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <button onClick={() => navigate('/workspace')} className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
          <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
          {t('返回工作台', 'Back')}
        </button>
        <div className="ml-auto">
          <Link to="/generate" className="px-4 py-1.5 text-[13px] rounded-lg bg-orange-500 text-white hover:bg-orange-600 font-medium transition-all">
            <Sparkles className="h-3.5 w-3.5 inline mr-1.5" />
            {t('新建官网', 'New Website')}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-xl font-semibold text-[#1a1a1a] mb-1">{t('我的官网', 'My Website')}</h1>
        <p className="text-sm text-gray-400 mb-8">{t('管理您的企业官网，编辑内容或发布上线', 'Manage your website, edit content or publish')}</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-gray-300">{t('加载中...', 'Loading...')}</div>
          </div>
        ) : !website ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
              <Globe className="h-8 w-8 text-orange-300" />
            </div>
            <h3 className="text-base font-medium text-[#1a1a1a] mb-2">{t('还没有官网', 'No website yet')}</h3>
            <p className="text-sm text-gray-400 mb-6">{t('一键生成您的专属企业官网，配备AI管家', 'Generate your dedicated website with AI Butler')}</p>
            <Link to="/generate" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-all">
              <Sparkles className="h-4 w-4" />
              {t('立即生成', 'Generate Now')}
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-black/[0.06] bg-white shadow-sm overflow-hidden">
            {/* Preview header */}
            <div className="h-40 bg-gradient-to-br from-violet-100 via-indigo-50 to-sky-50 flex items-center justify-center relative">
              <Globe className="h-12 w-12 text-violet-300" />
              <div className="absolute top-3 right-3">
                {(() => {
                  const s = statusConfig[website.status] || statusConfig.DRAFT
                  return (
                    <span className={`px-2.5 py-1 text-[11px] rounded-full ${s.bg} ${s.color} font-medium`}>
                      {s.label}
                    </span>
                  )
                })()}
              </div>
            </div>

            {/* Info */}
            <div className="p-6">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-1">{website.companyName}</h2>
              <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                <span>{website.industryType}</span>
                {website.shortcode && (
                  <span className="flex items-center gap-1">
                    · {getPublicUrl(website.shortcode)}
                    <button className="p-0.5 rounded hover:bg-gray-200 transition-colors"
                      title={t('复制链接', 'Copy link')}
                      onClick={() => navigator.clipboard.writeText(getPublicUrl(website.shortcode))}>
                      <Copy className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                    </button>
                  </span>
                )}
                {website.publishedAt && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {website.publishedAt}
                  </span>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-3 rounded-xl bg-black/[0.02]">
                  <div className="text-lg font-bold text-[#1a1a1a]">{website.pages?.length || 0}</div>
                  <div className="text-[11px] text-gray-400">{t('页面', 'Pages')}</div>
                </div>
                <div className="p-3 rounded-xl bg-black/[0.02]">
                  <div className="text-lg font-bold text-[#1a1a1a]">
                    {website.pages?.reduce((sum, p) => sum + (p.blocks?.length || 0), 0) || 0}
                  </div>
                  <div className="text-[11px] text-gray-400">{t('区块', 'Blocks')}</div>
                </div>
                <div className="p-3 rounded-xl bg-black/[0.02]">
                  <div className="text-lg font-bold text-[#1a1a1a]">
                    {website.butler?.enabled ? t('已启用', 'Active') : t('未启用', 'Off')}
                  </div>
                  <div className="text-[11px] text-gray-400">{t('AI管家', 'Butler')}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/website/${website.id}/edit`}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#1a1a1a] text-white text-sm font-medium hover:bg-[#333] transition-all"
                >
                  {t('编辑官网', 'Edit Website')}
                </Link>
                {website.status === 'PUBLISHED' ? (
                  <>
                    <a
                      href={`/e/${website.shortcode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-black/[0.08] text-sm text-[#1a1a1a] hover:bg-white transition-all"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('访问官网', 'Visit')}
                    </a>
                    <Link
                      to={`/website/${website.id}/analytics`}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-black/[0.08] text-sm text-gray-500 hover:text-[#1a1a1a] hover:bg-white transition-all"
                    >
                      <BarChart3 className="h-3.5 w-3.5" />
                      {t('数据分析', 'Analytics')}
                    </Link>
                  </>
                ) : (
                  <button
                    onClick={handlePublish}
                    disabled={publishing}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition-all"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {publishing ? t('发布中...', 'Publishing...') : t('发布上线', 'Publish')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
